/**
 * `/onboarding/account` — post-OTP first-fill of `name` + `email`.
 *
 * Visited by:
 *   - New users from `/sign-in` after their OTP verifies, before they
 *     hit `/onboarding/workspace` (no-membership outcome).
 *   - Returning users who somehow have an incomplete profile
 *     (existing-membership outcome with `profileComplete === false`).
 *
 * Either way, the root guard bounces anything else to this page until
 * `me.user.profileComplete === true`. The page calls
 * `identity.completeProfile`, refetches `me`, then dispatches:
 *   - existing membership → `/`
 *   - no membership       → `/onboarding/workspace`
 *
 * The team-invite path uses the same `AccountForm` but inline on
 * `/join/<token>` so the invitee never leaves their join URL — see
 * `apps/web/src/features/join/join-page.tsx`.
 *
 * @see ADR-0011 §3.5 — post-OTP profile completion
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  AccountForm,
  OnboardingShell,
  OnboardingHeader,
  Hero,
  HeroNote,
  BrandFor,
} from "../../../features/onboarding/components/index.ts";
import "../../../features/onboarding/components/onboarding.css";
import { meQueryOptions } from "../../../lib/session.ts";

export const Route = createFileRoute("/(auth)/onboarding/account")({
  component: OnboardingAccount,
});

function OnboardingAccount() {
  const navigate = useNavigate();
  const me = useQuery(meQueryOptions);

  const onComplete = () => {
    // Dispatch by membership state. After the form refetches `me`,
    // the data is fresh; if for some reason it isn't, the root guard
    // catches us on the next render and redirects again.
    if (me.data?.company) {
      void navigate({ to: "/" });
    } else {
      void navigate({ to: "/onboarding/workspace" });
    }
  };

  return (
    <OnboardingShell
      header={<OnboardingHeader />}
      brand={<BrandFor step="signIn" />}
      footer="One-time account setup · phone stays your sign-in credential"
    >
      <Hero
        kicker="Tell us who you are"
        title="Your name and a work email."
        description="Flint uses your name on every invoice you issue and your email on receipts and audit trails. Both are required so collaborators and customers can reach you."
      >
        <HeroNote>You can edit either later from your profile.</HeroNote>
      </Hero>
      <AccountForm me={me.data ?? null} onComplete={onComplete} />
    </OnboardingShell>
  );
}
